import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import {
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import AddIcon from "@mui/icons-material/Add";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import {
  useNeedsReviewOptionLabel,
  usePhysicianPickerQuery
} from "components/intake-physician-review";
import { INTAKE_PHYSICIAN_REVIEW_QUEUE_FEATURE } from "constants/feature-flags";
import { PhysicianFormValues } from "features/settings/physicians/physician-form.component";
import {
  useGetPhysicianQuery,
  useGetPhysiciansQuery
} from "services/physicians";
import { Physician } from "services/types/physician.type";
import { getFullName } from "utils/common";

import createPhysicianFromInput from "./physician-field.utils";
import PhysicianShortForm, {
  EMPTY_DEFAULT_VALUES
} from "./physician-short-form.component";

export type PhysicianOption = Physician & { isNew: boolean };

export const NEW_REFERRING_PHYSICIAN_OPTION: PhysicianOption = {
  id: -1,
  ...EMPTY_DEFAULT_VALUES,
  isNew: true
};

type PhysicianFieldProps = {
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  name: string;
  label?: string;
  patient?: number;
  allowNew?: boolean;
  showIcon?: boolean;
  excludeIds?: number[];
  /**
   * Drop physicians that are themselves waiting for intake review from the options (SN-19728).
   * For the one caller whose pick has to be a reviewed physician — the review dialog's link
   * target, which the server refuses when it is pending. Off by default: every other picker
   * deliberately keeps offering them, marked, so the front desk stops creating duplicates.
   */
  excludePendingReview?: boolean;
  transformOption?: (option: PhysicianOption) => PhysicianOption;
  sx?: SxProps;
  afterUpdate?: (value: PhysicianOption) => void;
  dropDownZIndex?: number;
};

export type RhfPhysicianField<T extends FieldValues> = PhysicianFieldProps &
  UseControllerProps<T>;

const getOptionLabel = (option: PhysicianOption) => {
  const fullName =
    option.full_name ||
    getFullName({
      firstName: option.first_name || "",
      lastName: option.last_name || ""
    });

  if (option.id === -1) {
    const addValue = fullName || option.phone || option.email;
    return `Add "${addValue}" as new Physician`;
  }
  return fullName;
};

export default function PhysicianField<T extends FieldValues>({
  control,
  required,
  disabled,
  name,
  label = "Physician",
  patient,
  allowNew = false,
  showIcon = false,
  readonly = false,
  transformOption,
  afterUpdate,
  excludeIds,
  excludePendingReview = false,
  shouldUnregister,
  dropDownZIndex,
  sx
}: RhfPhysicianField<T>) {
  const [openPhysicianForm, setOpenPhysicianForm] = useState<boolean>(false);

  const [physicianSearch, setPhysicianSearch] = useState<string>("");

  const [newPhysician, setNewPhysician] = useState<
    Partial<PhysicianFormValues>
  >({});

  const rules = useMemo(
    () => ({
      ...(required
        ? {
            required: `${label || name} is required`
          }
        : {})
    }),
    [label, name, required]
  );

  // Physicians waiting for intake review are offered like any other: without them here,
  // `allowNew` sends the front desk to `Add "..." as new Physician` and creates the duplicate
  // SN-19728 exists to prevent. `withNeedsReviewLabel` is what tells the two apart.
  const withNeedsReviewLabel = useNeedsReviewOptionLabel();
  const physicianPickerQuery = usePhysicianPickerQuery();
  // AND-ed with the flag: `intake_review_pending` is a filter an older backend does not know, and
  // sending it there would narrow nothing while risking a 400 — flag off means today's behaviour.
  const reviewQueueEnabled = Boolean(
    useFlags()[INTAKE_PHYSICIAN_REVIEW_QUEUE_FEATURE]
  );
  const { data, isLoading, refetch } = useGetPhysiciansQuery({
    is_active: true,
    patient,
    search: physicianSearch,
    ...(excludePendingReview && reviewQueueEnabled
      ? { intake_review_pending: false }
      : {}),
    query: physicianPickerQuery
  });

  const {
    fieldState: { error },
    field: { onChange, onBlur, value, ref },
    formState: { errors }
  } = useController({
    name,
    control,
    rules,
    shouldUnregister
  });

  // Fetch individual physician by ID when value is numeric and not in active list
  const { data: singlePhysician } = useGetPhysicianQuery(
    typeof value === "number" ? value : 0,
    { skip: typeof value !== "number" }
  );

  useEffect(() => {
    if (!isLoading && data && typeof value === "number") {
      const physician = data.find(
        (physician) => physician.id === Number(value)
      );

      if (physician) {
        onChange(physician);
      } else if (singlePhysician) {
        onChange(singlePhysician);
      }
    }
  }, [isLoading, data, value, onChange, singlePhysician]);

  const options = useMemo(() => {
    const excludeIdsSet = new Set(excludeIds || []);
    const dataWithNotNewPhysicians = (data || [])
      ?.map((physician) => ({
        ...physician,
        isNew: false
      }))
      .filter(
        (physician) => !(physician?.id && excludeIdsSet.has(physician.id))
      );

    const result = transformOption
      ? dataWithNotNewPhysicians.map(transformOption)
      : dataWithNotNewPhysicians;

    // Ensure current value is always in options to avoid MUI warning
    if (value && typeof value === "object" && value.id && value.id !== -1) {
      const valueInOptions = result.some((o) => o.id === value.id);
      if (!valueInOptions) {
        result.push({ ...value, isNew: false } as PhysicianOption);
      }
    }

    return result;
  }, [transformOption, data, excludeIds, value]);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={sx}
      >
        {showIcon && <EmojiPeopleIcon sx={{ mr: 1.5 }} color="action" />}
        <FormControl
          disabled={disabled}
          required={required}
          fullWidth
          error={Boolean(error?.message)}
          sx={{ label: { maxWidth: "100%" } }}
        >
          <Autocomplete
            fullWidth
            key={value || "physician"}
            slotProps={
              dropDownZIndex
                ? { popper: { sx: { zIndex: dropDownZIndex } } }
                : undefined
            }
            disabled={disabled}
            readOnly={readonly}
            popupIcon={<ExpandMoreOutlinedIcon />}
            inputValue={physicianSearch}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : withNeedsReviewLabel(getOptionLabel(option), option)
            }
            onInputChange={(_event, searchValue) =>
              setPhysicianSearch(searchValue)
            }
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                required={required}
                label={
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ pointerEvents: "all" }}
                  >
                    {label}{" "}
                    <Tooltip title="Add new">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNewPhysician({});
                          setOpenPhysicianForm(true);
                        }}
                        disabled={disabled}
                        color="success"
                        sx={{ padding: 0, border: "1px solid #ccc" }}
                      >
                        <AddIcon color="success" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                {...(Boolean(errors && errors[name]?.message)
                  ? { error: true, helperText: String(errors[name]?.message) }
                  : {})}
                placeholder="Search"
              />
            )}
            renderOption={(props: object, option: any) => (
              <ListItem {...props} key={option.id}>
                <ListItemText>
                  {withNeedsReviewLabel(getOptionLabel(option), option)}
                </ListItemText>
              </ListItem>
            )}
            value={value}
            ref={ref}
            onBlur={() => {
              if (!openPhysicianForm) {
                onBlur();
              }
            }}
            onChange={(_event, value: any) => {
              if (value && value.id === -1) {
                const {
                  first_name,
                  last_name,
                  title,
                  suffix,
                  npi,
                  phone,
                  email,
                  fax_phone
                } = value;
                setNewPhysician({
                  first_name,
                  last_name,
                  title,
                  suffix,
                  npi,
                  phone,
                  email,
                  fax_phone
                });
                setOpenPhysicianForm(true);
              } else {
                onChange(value);
                afterUpdate?.(value);
              }
            }}
            options={options}
            loading={isLoading}
            filterOptions={(options, params) => {
              const filtered = options.map((option) => option);
              if (allowNew && params.inputValue !== "") {
                const newPhysician = createPhysicianFromInput(
                  params.inputValue
                );
                filtered.push(newPhysician);
              }

              return filtered;
            }}
          />
        </FormControl>
      </Box>
      <PhysicianShortForm
        isOpen={openPhysicianForm}
        onCancel={() => setOpenPhysicianForm(false)}
        onSave={async (physician) => {
          await refetch();
          onChange({ ...physician, isNew: false });
          setOpenPhysicianForm(false);
        }}
        newPhysician={newPhysician}
      />
    </>
  );
}
